import React from "react";
import { LuChevronRight } from "react-icons/lu";
import "./sectionLabel.css";

// Shared subsection header for property / details / inspector panels.
// Renders an "eyebrow" label — small, uppercase, bold, muted, underlined —
// that sits below the panel title and above field content, giving a
// consistent visual hierarchy across all property pages in the app.
//
// Optionally it is also the section's SWITCH. Pass `collapsible` and the row becomes a button with
// a chevron at its end: pointing RIGHT at a closed section, turning DOWN to point at the content it
// opened. That is the disclosure convention — file trees, `<details>`, and AG Grid's own group rows
// in this very app — as opposed to the accordion one (down to open, up to close), which is for a
// row that reveals a panel rather than a section that unfolds in place.
//
// The caller owns `expanded` and does the hiding, because what "collapsed" means is the section's
// own business — Details keeps showing its title, while the sections around it go entirely.
//
// A caller that passes nothing gets exactly the div it always got. That matters: this control
// dresses a dozen panels across the app and only one of them is collapsible so far.
//
// Props:
//   collapsible — render the chevron and make the row a button
//   expanded    — which way the chevron points, and what `aria-expanded` says
//   onToggle    — () => void
export default function SectionLabel({ children, className, style, collapsible, expanded, onToggle }) {
  const cls = className ? `property-section-label ${className}` : "property-section-label";
  if (!collapsible) {
    return (
      <div className={cls} style={style}>
        {children}
      </div>
    );
  }
  return (
    // A real <button>, not a div with a click handler: this is a control, so it should be reachable
    // by Tab and pressable by Return like every other one.
    <button type="button" className={`${cls} property-section-toggle`} style={style}
      onClick={onToggle} aria-expanded={!!expanded}>
      <span>{children}</span>
      <LuChevronRight className={`property-section-chevron${expanded ? " is-expanded" : ""}`} />
    </button>
  );
}
